
/**
 * TextEmbeddingVisualizer.jsx
 *
 * Main React component for the text embedding visualization app.
 * Handles all UI state, data flow, and orchestrates the Three.js scene and overlays.
 *
 * Features:
 * - Uses ThreeSceneManager to manage Three.js scene, renderer, camera, and VR logic.
 * - Uses EmbeddingsGroup to render all 3D objects (points, lines, curve, labels).
 * - Provides UI panels for controls, chunk list, and PCA matrix.
 * - Handles text input, tokenization, chunking, and embedding computation.
 * - Supports VR mode and importing/exporting visualization state.
 *
 * Author: [Your Name]
 * Date: [Update as needed]
 */


import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTextEmbeddings } from '../hooks/useTextEmbeddings';
import ThreeSceneManager from './ThreeSceneManager';
import EmbeddingsGroup from './EmbeddingsGroup';
import ControlsPanel from './ControlsPanel';
import { PointStyle } from '../objects/CustomPointHelper';
import {
  DisplayParamTokenColorMode,
  DisplayParamEnvironmentColorMode,
  DisplayParamFogColorMode,
  DisplayParamLabelColorMode
} from '../constants';
import ChunkList from './ChunkList';
import Box from '@mui/material/Box';
import { getTokenColor, getSequenceColor } from '../utils/colorUtils';
import { buildChunks } from '../utils/buildChunks';
import { fetchTokensFromText } from '../utils/tokenizer';
import debounce from 'lodash.debounce';
import PCAMatrixPanel from './PCAMatrixPanel';
import { usePoems } from '../hooks/usePoems';
import { buildVisualizationSaveObject } from '../utils/buildVisualizationSaveObject';
import { downloadJSON } from '../utils/fileUtils';
import { validateVisualizationData } from '../utils/validateVisualizationData';


/**
 * @typedef {import('../types/chunk').Chunk} Chunk
 * @typedef {import('../types/token').Token} Token
 * @typedef {import('../types/embedding').Embedding} Embedding
 * @typedef {import('../types/pca').PCA} PCA
 * @typedef {import('../types/embeddingModelInfo').EmbeddingModelInfo} EmbeddingModelInfo
 * @typedef {import('../types/visualizationParams').VisualizationParams} VisualizationParams
 */


// Example using Google Fonts CDN (Roboto Regular)
const robotoFontUrl = 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf';

/**
 * TextEmbeddingVisualizer
 *
 * Main visualization component. Manages all UI state, data flow, and coordinates the 3D scene.
 *
 * @component
 * @param {Object} props
 * @param {function} props.tokenizer - Tokenizer function for text input
 * @returns {JSX.Element}
 */
export default function TextEmbeddingVisualizer({ tokenizer }) {
  // Point style state for 3D points
  const [pointStyle, setPointStyle] = useState(PointStyle.CIRCLE);

  // Handler for point style changes from UI
  const handleChangePointStyle = React.useCallback((style) => {
    console.debug('[TextEmbeddingVisualizer] handleChangePointStyle:', style);
    setPointStyle(style);
  }, []);
  // Ref to the canvas element for Three.js rendering
  const canvasRef = useRef(null);
  
  // Ref to the ThreeSceneManager for accessing scene/camera/renderer/VR handler
  const sceneManagerRef = useRef(null);

  // --------------------
  // UI and Visualization State
  // --------------------
  // Main UI state variables
  const [inputText, setInputText] = useState(''); // User input text
  const [tokens, setTokens] = useState([]); // Tokenized input
  const [showTokenIds, setShowTokenIds] = useState(false); // Toggle for showing token IDs
  // Separate color mode state for input text panel and display parameters panel
  const [setTextPanel_tokenColorMode] = useState(DisplayParamTokenColorMode.SEQUENCE);
  const [displayParam_tokenColorMode, setDisplayParam_tokenColorMode] = useState(DisplayParamTokenColorMode.SEQUENCE);
  const [displayParam_environmentColorMode, setDisplayParam_environmentColorMode] = useState(DisplayParamEnvironmentColorMode.BLACK);
  const [displayParam_fogColorMode, setDisplayParam_fogColorMode] = useState(DisplayParamFogColorMode.NONE);
  // Label coloring mode for display parameters panel
  const [displayParam_labelColorMode, setDisplayParam_labelColorMode] = useState(DisplayParamLabelColorMode.SEQUENCE);
  const [windowSize, setWindowSize] = useState(16); // Chunk window size
  const [overlap, setOverlap] = useState(15); // Chunk overlap
  const [embeddingDimensions, setEmbeddingDimensions] = useState(1536); // Embedding dimension
  const [showLabels, setShowLabels] = useState(true); // Show first-word labels
  const [chunks, setChunks] = useState([]); // Chunks/windows of tokens
  const [textSize, setTextSize] = useState(2); // Text label size
  const [dotSize, setDotSize] = useState(1);   // Point size
  const [isTokenizing, setIsTokenizing] = useState(false); // Tokenization state
  const [isChunking, setIsChunking] = useState(false); // Chunking state
  const [labelOffset, setLabelOffset] = useState(0.0); // Vertical offset for labels
  const [highlightedIndex, setHighlightedIndex] = useState(0); // Highlighted label index
  const [showPathMode, setShowPathMode] = useState('curve'); // Path display mode
  const [showHelpers, setShowHelpers] = useState(true); // Show 3D helpers (axes/grid)
  const [showPCAMatrixPanel, setShowPCAMatrixPanel] = useState(false); // Show PCA matrix panel
  const [isImporting, setIsImporting] = useState(false); // Importing state
  //const [pcaResult, setPcaResult] = useState(null);

  // Poem selection state (for demo/sample text)
  const { poems, loading: poemsLoading, error: poemsError } = usePoems();
  const [selectedPoemIndex, setSelectedPoemIndex] = useState('');

  // Memoize poems array for performance
  const poemsMemo = React.useMemo(() => poems, [poems]);
  const handleSelectPoem = React.useCallback(idx => setSelectedPoemIndex(idx), []);

  // When a poem is selected, update the input text
  useEffect(() => {
    console.log('[TextEmbeddingVisualizer][useEffect] Selected poem index changed:', selectedPoemIndex);
    if (
      selectedPoemIndex !== '' &&
      poems[selectedPoemIndex] &&
      poems[selectedPoemIndex].text
    ) {
      setInputText(poems[selectedPoemIndex].text);
    }
  }, [selectedPoemIndex, poems]);

  // Compute embeddings and PCA using custom hook
  const { enrichedChunks, isLoading, processChunks, pcaResult } = useTextEmbeddings({
    text: inputText,
    windowSize,
    overlap,
    embeddingDimensions,
    tokenizer,
  });

  // Update tokens on every input change
  const handleInputTextChange = useCallback((text) => {
    setInputText(text);
  }, []);


  // VR handler (delegated to ThreeSceneManager)
  const handleEnterVR = useCallback(() => {
    if (sceneManagerRef.current && sceneManagerRef.current.handleEnterVR) {
      sceneManagerRef.current.handleEnterVR();
    }
  }, []);


  // Keep refs for latest values (for debounced tokenization/chunking)
  const windowSizeRef = useRef(windowSize);
  const overlapRef = useRef(overlap);

  useEffect(() => { windowSizeRef.current = windowSize; }, [windowSize]);
  useEffect(() => { overlapRef.current = overlap; }, [overlap]);

  // Debounced tokenization to avoid excessive computation on every keystroke
  const debouncedTokenize = useRef(
    debounce(async (text) => {
      const toks = await fetchTokensFromText(text);
      setTokens(toks);
      setChunks(buildChunks(toks, windowSizeRef.current, overlapRef.current));
      setIsTokenizing(false);
    }, 1000)
  ).current;

  // Tokenize only when inputText changes
  useEffect(() => {
    if (isImporting) {
      console.log('[TextEmbeddingVisualizer][useEffect] Skipping tokenization during import');
      return; // Skip tokenization during import
    }
    else
    {
      console.log('[TextEmbeddingVisualizer][useEffect] Tokenizing effect triggered:', { inputText });
    }
    if (!inputText) {
      setTokens([]);
      setChunks([]);
      setIsTokenizing(false);
      return;
    }
    setIsTokenizing(true);
    debouncedTokenize(inputText);
    return () => debouncedTokenize.cancel();
  }, [inputText, debouncedTokenize, isImporting]);

  // Debounced chunking to avoid excessive computation
  const debouncedChunk = useRef(
    debounce((tokens, windowSize, overlap, setChunks, setIsChunking) => {
      setChunks(buildChunks(tokens, windowSize, overlap));
      setIsChunking(false);
    }, 1000)
  ).current;



  // Chunk tokens when tokens, windowSize, or overlap change
  useEffect(() => {
    if (isImporting) { 
      console.log('[TextEmbeddingVisualizer][useEffect] Skipping chunking during import');
      return; // Skip chunking during import
    } else {
      console.log('[TextEmbeddingVisualizer][useEffect] Chunking effect triggered:', { tokens, windowSize, overlap });
    }
    if (tokens && tokens.length > 0) {
      setIsChunking(true);
      debouncedChunk(tokens, windowSize, overlap, setChunks, setIsChunking);
      return () => debouncedChunk.cancel();
    } else {
      setIsChunking(false);
    }
  }, [tokens, windowSize, overlap, debouncedChunk, isImporting]);

  // Handler to trigger embedding computation and visualization
  const handleGenerateVisualization = useCallback(async () => {
    await processChunks(chunks);
  }, [processChunks, chunks]);


  // (Optional) useEffect for automatic label highlighting, if desired
  // Automatically highlight labels every second
  // useEffect(() => {
  //   console.log('[TextEmbeddingVisualizer][useEffect] Automatic label highlighting effect triggered:', { enrichedChunksLength: enrichedChunks.length });
  //   if (!enrichedChunks.length) return;
  //   const interval = setInterval(() => {
  //     setHighlightedIndex(idx => (idx + 1) % enrichedChunks.length);
  //   }, 1000);
  //   return () => clearInterval(interval);
  // }, [enrichedChunks.length]);



  // Compute if the visualization is ready to be saved (all data present, not loading)
  const readyToSave =
    enrichedChunks && enrichedChunks.length > 0 &&
    pcaResult && pcaResult.pcaMatrix && pcaResult.pcaMatrix.length > 0 &&
    !isLoading && !isTokenizing && !isChunking;

  // Map enrichedChunks to match schema (embeddingReduced property)
  const schemaChunks = enrichedChunks.map(chunk => ({
    ...chunk,
    chunkEmbeddingReduced: {
      fullEmbedding: chunk.chunkEmbeddingReduced?.fullEmbedding ?? [],
      dimensions: chunk.chunkEmbeddingReduced?.dimensions ?? 3
    }
  }));

  /**
   * Save the current visualization state as a JSON file.
   * Called when the user clicks the Save button.
   */
  const handleSave = async () => {
    console.log('[TextEmbeddingVisualizer][handleSave] Saving visualization data...');
    // Camera/controls/rig params are not available in this refactored version
    const cameraParams = undefined;
    const saveData = buildVisualizationSaveObject({
      inputText,
      tokens,
      chunks: schemaChunks,
      pcaResult,
      embeddingModelInfo: {
        name: "text-embedding-3-small", // by default we will use OpenAI's text-embedding-3-small
        dimensions: embeddingDimensions,
        modelVersion: "text-embedding-3-small" // for openai, the version is the model name
      },
      chunking: { windowSize, overlap },
      visualizationParams: {
        colorMode: displayParam_tokenColorMode,
        showLabels,
        textSize,
        dotSize,
        labelOffset,
        ...(cameraParams ? { camera: cameraParams } : {})
      }
    });
    console.log('[TextEmbeddingVisualizer][handleSave] Save data:', saveData);
    downloadJSON(saveData, "tev_visualization");
    // await fetch('/api/save', { method: 'POST', body: JSON.stringify(saveData) });
  };

  /**
   * Import a saved visualization state from JSON.
   * @param {Object} data - Imported visualization data
   */
  const handleOpen = useCallback((data) => {
    console.log('[TextEmbeddingVisualizer][handleOpen] Importing data:', data);
    // 1. Check for missing data
    if (!data) {
      console.error('[TextEmbeddingVisualizer][handleOpen] No data provided for import.');
      return;
    }
    // 2. Validate against JSON schema
    if (!validateVisualizationData(data)) {
      console.error('[TextEmbeddingVisualizer][handleOpen] Invalid data format:', data);
      return;
    }
    console.log('[TextEmbeddingVisualizer][handleOpen] Data is valid:', data);
    // At this point, all required fields should exist
    // 3. Announce importing to system and set importing flag
    setIsImporting(true);
    // 4. (No longer clearing Three.js objects/groups directly)
    // 5. Reset all React state to initial values
    resetAllState();
    console.log('[TextEmbeddingVisualizer][handleOpen] Reset all state to initial values.');
    // 6. Set all state from imported file
    console.log('[TextEmbeddingVisualizer][handleOpen] Set inputText:', data.inputText);
    setInputText(data.inputText);
    console.log('[TextEmbeddingVisualizer][handleOpen] Set tokens:', data.tokens);
    setTokens(data.tokens);
    console.log('[TextEmbeddingVisualizer][handleOpen] Set chunks:', data.chunks);
    setChunks(data.chunks);
    //console.log('[TextEmbeddingVisualizer][handleOpen] Set pcaResult:', data.pcaResult);
    //setPcaResult(data.pcaResult);
    console.log('[TextEmbeddingVisualizer][handleOpen] Set embeddingDimensions:', data.embeddingModelInfo.dimensions);
    setEmbeddingDimensions(data.embeddingModelInfo.dimensions);
    console.log('[TextEmbeddingVisualizer][handleOpen] Set windowSize:', data.chunking.windowSize);
    setWindowSize(data.chunking.windowSize);
    setOverlap(data.chunking.overlap);
    //setColorMode(data.visualizationParams.colorMode);
    setShowLabels(data.visualizationParams.showLabels);
    setTextSize(data.visualizationParams.textSize);
    setDotSize(data.visualizationParams.dotSize);
    setLabelOffset(data.visualizationParams.labelOffset);
    setShowPathMode(data.visualizationParams.showPathMode || 'curve'); // Default to 'curve' if not set
    // Validate and set color/fog/environment modes using enums
    setDisplayParam_tokenColorMode(
      Object.values(DisplayParamTokenColorMode).includes(data.visualizationParams.colorMode)
        ? data.visualizationParams.colorMode
        : DisplayParamTokenColorMode.SEQUENCE
    );
    setDisplayParam_environmentColorMode(
      Object.values(DisplayParamEnvironmentColorMode).includes(data.visualizationParams.environmentColorMode)
        ? data.visualizationParams.environmentColorMode
        : DisplayParamEnvironmentColorMode.BLACK
    );
    setDisplayParam_fogColorMode(
      Object.values(DisplayParamFogColorMode).includes(data.visualizationParams.fogColorMode)
        ? data.visualizationParams.fogColorMode
        : DisplayParamFogColorMode.NONE
    );
    // Validate pointStyle before setting
    const allowedPointStyles = [PointStyle.CIRCLE, PointStyle.RING, PointStyle.CLOUD];
    const importedPointStyle = data.visualizationParams.pointStyle;
    setPointStyle(allowedPointStyles.includes(importedPointStyle) ? importedPointStyle : PointStyle.CIRCLE); // Default to circle if not set or invalid
    setShowHelpers(data.visualizationParams.showHelpers);
    setShowPCAMatrixPanel(true); 
    // setHighlightedIndex(0);
    // // 7. Optionally restore camera/rig state if present
    // if (data.visualizationParams?.camera) {
    //   // You may want to set camera/controls/rig state here
    //   // Example: setCameraParams(data.visualizationParams.camera);
    //   console.log('[TextEmbeddingVisualizer][handleOpen] Camera params found in imported data:', data.visualizationParams.camera);
    // }
    // 8. Clear importing flag after state is set (allowing React to update)
    setTimeout(() => setIsImporting(false), 0);
    console.log('[TextEmbeddingVisualizer][handleOpen] Import complete.');
  }, []);

  // Reset all relevant state to initial values before importing new data
  /**
   * Reset all UI and visualization state to initial values.
   * Called before importing a new visualization state.
   */
  function resetAllState() {
    setInputText('');
    setTokens([]);
    setChunks([]);
    setEmbeddingDimensions(1536);
    setWindowSize(16);
    setOverlap(15);
    setDisplayParam_tokenColorMode(DisplayParamTokenColorMode.SEQUENCE);
    setDisplayParam_environmentColorMode(DisplayParamEnvironmentColorMode.BLACK);
    setDisplayParam_fogColorMode(DisplayParamFogColorMode.NONE);
    setShowLabels(true);
    setTextSize(2);
    setDotSize(1);
    setLabelOffset(0.0);
    setShowPathMode('curve');
    setPointStyle(PointStyle.CIRCLE);
    setShowHelpers(true);
    setShowPCAMatrixPanel(false);
    setHighlightedIndex(0);
    // Add any other state resets as needed
  }
  // --------------------
  // Render
  // --------------------
  // Main render: layout the 3D scene, overlays, and control panels
  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#f8fafc',
      }}
    >
      {/* 3D Canvas and Three.js scene */}
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          display: 'block',
          zIndex: 0,
        }}
      />
      {/* Three.js scene manager and embeddings group */}
      <ThreeSceneManager ref={sceneManagerRef} canvasRef={canvasRef} showHelpers={showHelpers}>
        <EmbeddingsGroup
          sceneRef={sceneManagerRef.current ? sceneManagerRef.current.sceneRef : { current: null }}
          threeSceneManagerRef={sceneManagerRef}
          enrichedChunks={enrichedChunks}
          displayParam_tokenColorMode={displayParam_tokenColorMode}
          displayParam_fogColorMode={displayParam_fogColorMode}
          displayParam_environmentColorMode={displayParam_environmentColorMode}
          displayParam_labelColorMode={displayParam_labelColorMode}
          showLabels={showLabels}
          textSize={textSize}
          dotSize={dotSize}
          labelOffset={labelOffset}
          showPathMode={showPathMode}
          getTokenColor={getTokenColor}
          getSequenceColor={getSequenceColor}
          pointStyle={pointStyle}
        />
      </ThreeSceneManager>

      {/* Overlay Panels (controls, chunk list, PCA matrix) */}
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          left: 24,
          zIndex: 10,
          pointerEvents: 'auto',
          maxWidth: 400,
          minWidth: 320,
        }}
      >
        <ControlsPanel
          inputText={inputText}
          tokens={tokens}
          chunks={chunks}
          showTokenIds={showTokenIds}
          onInputTextChange={handleInputTextChange}
          onToggleShowTokenIds={setShowTokenIds}
          textPanel_tokenColorMode={displayParam_tokenColorMode}
          onChangeTextPanel_tokenColorMode={setTextPanel_tokenColorMode}
          displayParam_tokenColorMode={displayParam_tokenColorMode}
          onChangeDisplayParam_tokenColorMode={setDisplayParam_tokenColorMode}
          displayParam_environmentColorMode={displayParam_environmentColorMode}
          onChangeDisplayParam_environmentColorMode={setDisplayParam_environmentColorMode}
          displayParam_fogColorMode={displayParam_fogColorMode}
          onChangeDisplayParam_fogColorMode={setDisplayParam_fogColorMode}
          displayParam_labelColorMode={displayParam_labelColorMode}
          onChangeDisplayParam_labelColorMode={setDisplayParam_labelColorMode}
          windowSize={windowSize}
          onWindowSizeChange={setWindowSize}
          overlap={overlap}
          onOverlapChange={setOverlap}
          embeddingDimensions={embeddingDimensions}
          onEmbeddingDimensionsChange={setEmbeddingDimensions}
          onGenerateVisualization={handleGenerateVisualization}
          isLoading={isLoading}
          isTokenizing={isTokenizing}
          showLabels={showLabels}
          onToggleShowLabels={setShowLabels}
          textSize={textSize}
          onTextSizeChange={setTextSize}
          dotSize={dotSize}
          onDotSizeChange={setDotSize}
          isChunking={isChunking}
          setIsChunking={setIsChunking}
          labelOffset={labelOffset}
          onLabelOffsetChange={setLabelOffset}
          showPathMode={showPathMode}
          onChangeShowPathMode={setShowPathMode}
          showHelpers={showHelpers}
          onToggleShowHelpers={setShowHelpers}
          showPCAMatrix={showPCAMatrixPanel}
          onToggleShowPCAMatrix={setShowPCAMatrixPanel}
          poems={poemsMemo}
          selectedPoemIndex={selectedPoemIndex}
          onSelectPoem={handleSelectPoem}
          getTokenColor={getTokenColor}
          getSequenceColor={getSequenceColor}
          readyToSave={readyToSave}
          handleSave={handleSave}
          onOpenTevJson={handleOpen}
          onEnterVR={handleEnterVR}
          onChangePointStyle={handleChangePointStyle}
          pointStyle={pointStyle}
        />
      </Box>
      {/* Chunk list panel (right side) */}
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          right: 24,
          zIndex: 10,
          pointerEvents: 'auto',
          maxWidth: 400,
          minWidth: 320,
          height: 'calc(100vh - 100px)',
          overflow: 'hidden'
        }}
      >
        {chunks.length > 0 && !isChunking && (
          <ChunkList
            chunks={chunks}
            allTokens={tokens}
            chunkList_tokenColorMode={displayParam_tokenColorMode}
            getTokenColor={getTokenColor}
            getSequenceColor={getSequenceColor}
          />
        )}
      </Box>
      {/* PCA matrix panel (bottom) */}
      {pcaResult && pcaResult.pcaMatrix && pcaResult.pcaMatrix.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            left: 24,
            right: 24,
            bottom: 24,
            zIndex: 20,
            pointerEvents: 'auto',
            bgcolor: 'rgba(255,255,255,0.95)',
            borderRadius: 2,
            boxShadow: 3,
            p: 0,
          }}
        >
          <PCAMatrixPanel pcaResult={pcaResult} cellHeight={8} />
        </Box>
      )}
    </Box>
  );
}

